import api from "./apiClient";

/* ===================================================== */
/* 🔧 ERROR NORMALIZER                                   */
/* ===================================================== */

function normalizeError(err, fallbackMessage) {
  return new Error(
    err?.response?.data?.message ||
    err?.message ||
    fallbackMessage
  );
}

/* ===================================================== */
/* 📦 RESPONSE NORMALIZER                                */
/* Handles { task } vs direct object                     */
/* ===================================================== */

function extractTask(data) {
  if (!data) return null;
  return data.task || data;
}

/* ===================================================== */
/* 📥 GET TASKS (GENERAL ONLY)                           */
/* GET /tasks/:projectId                                 */
/* ===================================================== */

export const getTasks = async (projectId, filters = {}) => {
  if (!projectId) {
    throw new Error("Project ID is required");
  }

  try {
    const { data } = await api.get(`/tasks/project/${projectId}`, {
      params: filters,
    });

    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw normalizeError(err, "Failed to fetch tasks");
  }
};

/* ===================================================== */
/* ➕ CREATE TASK (NO PHASE)                             */
/* POST /tasks                                           */
/* ===================================================== */

export const createTask = async (projectId, payload = {}) => {
  if (!projectId) {
    throw new Error("Project ID is required");
  }

  const title = payload?.title?.trim();

  if (!title) {
    throw new Error("Task title is required");
  }

  try {
    const { data } = await api.post(`/tasks`, {
      ...payload,
      project: projectId,
      title,
    });

    return extractTask(data);
  } catch (err) {
    throw normalizeError(err, "Failed to create task");
  }
};

/* ===================================================== */
/* 🔄 UPDATE TASK                                        */
/* PUT /tasks/:taskId                                    */
/* ===================================================== */

export const updateTask = async (taskId, payload = {}) => {
  if (!taskId) {
    throw new Error("Task ID is required");
  }

  try {
    const { data } = await api.put(`/tasks/${taskId}`, payload);
    return extractTask(data);
  } catch (err) {
    throw normalizeError(err, "Failed to update task");
  }
};

/* ===================================================== */
/* ✅ STATUS HELPERS                                     */
/* ===================================================== */

export const setTaskStatus = async (taskId, status) => {
  if (!taskId) throw new Error("Task ID is required");
  if (!status) throw new Error("Status is required");

  return updateTask(taskId, { status });
};

export const toggleTaskStatus = async (task) => {
  if (!task?._id) {
    throw new Error("Valid task object required");
  }

  const nextStatus =
    task.status === "completed" ? "pending" : "completed";

  return setTaskStatus(task._id, nextStatus);
};

/* ===================================================== */
/* 🗑 DELETE TASK                                        */
/* DELETE /tasks/:taskId                                 */
/* ===================================================== */

export const deleteTask = async (taskId) => {
  if (!taskId) {
    throw new Error("Task ID is required");
  }

  try {
    await api.delete(`/tasks/${taskId}`);
    return true;
  } catch (err) {
    throw normalizeError(err, "Failed to delete task");
  }
};

/* ===================================================== */
/* 📎 ATTACHMENT UPLOAD                                  */
/* POST /tasks/:taskId/attachments                       */
/* ===================================================== */

export const uploadTaskAttachment = async (taskId, file) => {
  if (!taskId) {
    throw new Error("Task ID is required");
  }

  if (!file) {
    throw new Error("File is required");
  }

  try {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await api.post(
      `/tasks/${taskId}/attachments`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return extractTask(data);
  } catch (err) {
    throw normalizeError(err, "Failed to upload attachment");
  }
};

/* ===================================================== */
/* 📦 EXPORT                                             */
/* ===================================================== */

const taskService = {
  getTasks,
  createTask,
  updateTask,
  deleteTask,
  setTaskStatus,
  toggleTaskStatus,
  uploadTaskAttachment,
};

export default taskService;